import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { CalendarCheckIcon, CornerDownRightIcon, LoaderCircleIcon, LockIcon, LockOpenIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import { closeChecks, lateChanges, negativeCompLoans, overrideLines, parityStats, payeeSummaries, periodStats } from '../data/periodClose';
import { lockedPeriods, manualLinePayees } from '../data/people';
import { Button } from '../components/sf/Button';
import { NewBadge } from '../components/sf/NewBadge';
import { RecordHeader } from '../components/sf/RecordHeader';
import { ScopedNotification } from '../components/sf/ScopedNotification';
import { StatusBadge } from '../components/sf/StatusBadge';
import { Tooltip } from '../components/sf/Tooltip';
import { ManualLineModal, ManualLineMode } from '../components/commission/ManualLineModal';
import { ValidationCard } from '../components/compPlan/ValidationCard';
import { CloseSummary } from '../components/periodClose/CloseSummary';
import { LateChangesCard } from '../components/periodClose/LateChangesCard';
import { LockPeriodModal } from '../components/periodClose/LockPeriodModal';
import { NegativeCompCard } from '../components/periodClose/NegativeCompCard';
import { ParityCard } from '../components/periodClose/ParityCard';
import { PayeeTable } from '../components/periodClose/PayeeTable';
import { OverrideLinesCard } from '../components/periodClose/OverrideLinesCard';
import { UnlockPeriodModal } from '../components/periodClose/UnlockPeriodModal';
import { ManualLineInput } from '../types/commission';
import { ScreenId } from '../types/navigation';
import { payeeStoredTotal } from '../utils/commission';
import { formatUsd, sumCents } from '../utils/format';
interface PeriodCloseProps {
  onNavigate: (screen: ScreenId) => void;
}
type ModalState = 'lock' | 'unlock' | ManualLineMode | null;
export function PeriodClose({
  onNavigate
}: PeriodCloseProps) {
  const [isLocked, setIsLocked] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [extraLines, setExtraLines] = useState(0);
  const payTotal = sumCents(payeeSummaries.map(payeeStoredTotal));
  const adjustedTotal = sumCents(payeeSummaries.map(payee => payee.adjustedTotal));
  const passed = closeChecks.filter(check => check.status === 'pass').length;
  const nextOpen = isLocked ? 'November 2026' : 'October 2026';
  const closeModal = () => setModal(null);
  function handleRecalculate() {
    setIsRecalculating(true);
    window.setTimeout(() => {
      setIsRecalculating(false);
      toast.success('Recalculation queued for October 2026', {
        description: `${periodStats.loans} loans will be re-run. Late changes will be folded into October lines.`
      });
    }, 1000);
  }
  function handleLock() {
    setIsLocked(true);
    closeModal();
    toast.success('October 2026 locked for every employee', {
      description: `${lateChanges.length} late changes posted as Adjustment lines to November 2026.`
    });
  }
  function handleUnlock(reason: string) {
    setIsLocked(false);
    closeModal();
    toast.success('October 2026 unlocked', {
      description: `Reason logged: “${reason}”`
    });
  }
  function handleManualLine(input: ManualLineInput) {
    const mode = modal;
    closeModal();
    if (!isLocked && mode === 'manual') setExtraLines(count => count + 1);
    toast.success(mode === 'adjustment' ? `Adjustment posted to ${nextOpen}` : `Manual line added to ${nextOpen}`, {
      description: `${input.payee} · ${formatUsd(input.amount)}${input.splitPct !== null ? ` · ${input.splitPct}% split` : ''} — “${input.reason}”`
    });
  }
  return <div className="space-y-3" data-id="element-1219" data-mp="wkzysdjph1o2">
      <RecordHeader objectLabel="Commission Period Close" title="Commission Period — October 2026" icon={CalendarCheckIcon} iconBg="#06A59A" titleAddon={<StatusBadge status={isLocked ? 'Locked' : 'Open'} data-id="element-1221" data-mp="33cg33cvxu3vh" />} actions={<div className="flex flex-col items-end gap-1" data-id="element-1222" data-mp="33ad1rwpc4obg">
            <div className="flex items-center gap-2" data-id="element-1223" data-mp="37eku03v04mef">
              <Button icon={<PlusIcon data-id="element-1225" data-mp="3cccj6q49huex" />} onClick={() => setModal('manual')} data-id="element-1224" data-mp="3cd4gq6hf627u">
                Add Manual Line
              </Button>
              <Button icon={<CornerDownRightIcon data-id="element-1227" data-mp="3h55nxdzqd2r0" />} onClick={() => setModal('adjustment')} data-id="element-1226" data-mp="3h4meyms25op4">
                Add Adjustment
              </Button>
              {isLocked ? <Tooltip content="October 2026 is locked. Recalculate November 2026 instead." side="bottom" align="end" data-id="element-1228" data-mp="3r18zfagh2dqm">
                  <Button disabled icon={<RefreshCwIcon data-id="element-1230" data-mp="3v45ckln8thn6" />} data-id="element-1229" data-mp="3v4f1e5wjxira">
                    Recalculate Period
                  </Button>
                </Tooltip> : <Button onClick={handleRecalculate} disabled={isRecalculating} title="NEW — not in main today" icon={isRecalculating ? <LoaderCircleIcon className="animate-spin" data-id="element-1232" data-mp="1y4mx13rnom3a" /> : <RefreshCwIcon data-id="element-1233" data-mp="1y6hiqkar4i8f" />} data-id="element-1231" data-mp="20puo4eak8phx">
                  {isRecalculating ? 'Recalculating…' : 'Recalculate Period (NEW)'}
                </Button>}
              {isLocked ? <Button icon={<LockOpenIcon data-id="element-1235" data-mp="1od0ppiifv75t" />} onClick={() => setModal('unlock')} data-id="element-1234" data-mp="1ofmytmkz9uq5">
                  Unlock (admin)
                </Button> : <Button variant="brand" icon={<LockIcon data-id="element-1237" data-mp="2h38xqhj9gb6o" />} onClick={() => setModal('lock')} data-id="element-1236" data-mp="2h40v9xwf4izl">
                  Lock Period
                </Button>}
            </div>
            <p className="flex items-center gap-1.5 text-xs text-sf-weak" data-id="element-1238" data-mp="275banxd50kfn">
              <NewBadge data-id="element-1239" data-mp="277fp398nx1u6" />
              {isLocked ? 'Unlock requires an admin and a reason.' : 'Lock Period locks every employee’s October 2026 period.'}
            </p>
          </div>} fields={[{
      label: 'Period',
      value: 'Oct 1 – Oct 31 2026'
    }, {
      label: 'Plan Version',
      value: <span className="text-sf-blue" data-id="element-1240" data-mp="rr74ld6xax6p">v1 parity (seed 2026-v1-parity)</span>
    }, {
      label: 'Next Open Period',
      value: 'November 2026'
    }]} data-id="element-1220" data-mp="8wqexh61ynzk" />

      <AnimatePresence initial={false} data-id="element-1241" data-mp="y4ks5mqddonk">
        {isLocked && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1242" data-mp="j8byr1t5u5j2">
            <ScopedNotification tone="success" icon={LockIcon} data-id="element-1243" data-mp="aficulj7k7w8">
              <strong data-id="element-1244" data-mp="adxfko5ieauo">Locked just now.</strong> Changes will post to November 2026. <NewBadge data-id="element-1245" data-mp="f6gz01qvo07e" />
            </ScopedNotification>
          </motion.div>}
      </AnimatePresence>

      <CloseSummary storedTotal={payTotal} adjustedTotal={adjustedTotal} payeeCount={payeeSummaries.length} loans={periodStats.loans} lines={periodStats.lines + extraLines} adjustmentsPending={isLocked ? 0 : periodStats.adjustmentsPending} negativeComp={negativeCompLoans.length} isLocked={isLocked} data-id="element-1246" data-mp="infi5yo8djj" />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2" data-id="element-1247" data-mp="38ytj90lmiuq7">
        <ValidationCard title="Pre-close checklist" checks={closeChecks} meta={`${passed} of ${closeChecks.length} passed · ${closeChecks.length - passed} notices`} data-id="element-1248" data-mp="3408huxumy506" />
        <div className="space-y-3" data-id="element-1249" data-mp="3i1b5vg56xpdq">
          <ParityCard legacyAdjusted={parityStats.legacyAdjusted} legacyStored={parityStats.legacyStored} newAdjustedTotal={adjustedTotal} newStoredTotal={payTotal} mismatches={parityStats.mismatches} data-id="element-1250" data-mp="3d0wz01fbslha" />
          <LateChangesCard changes={lateChanges} isLocked={isLocked} data-id="element-1251" data-mp="3mx09pvo8tsfu" />
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2" data-id="element-1252" data-mp="4kby77wukyp9">
        <OverrideLinesCard lines={overrideLines} data-id="element-1253" data-mp="kt87xq65xfk" />
        <NegativeCompCard loans={negativeCompLoans} data-id="element-1254" data-mp="ejavjuc5x7wy" />
      </div>

      <PayeeTable payees={payeeSummaries} onViewDashboard={() => onNavigate('statement')} data-id="element-1255" data-mp="ejkjlcdnd500" />

      <LockPeriodModal open={modal === 'lock'} payTotal={payTotal} adjustedTotal={adjustedTotal} payeeCount={payeeSummaries.length} lineCount={periodStats.lines + extraLines} lateChangeCount={lateChanges.length} onClose={closeModal} onConfirm={handleLock} data-id="element-1256" data-mp="9lrdejccgazq" />
      <UnlockPeriodModal open={modal === 'unlock'} onClose={closeModal} onConfirm={handleUnlock} data-id="element-1257" data-mp="1pvivasn2u7tm" />
      <ManualLineModal open={modal === 'manual' || modal === 'adjustment'} mode={modal === 'adjustment' ? 'adjustment' : 'manual'} context={`Commission Period — ${nextOpen}`} payees={manualLinePayees} defaultPayee={manualLinePayees[0]} postsTo={nextOpen} lockedPeriods={lockedPeriods} onClose={closeModal} onSubmit={handleManualLine} data-id="element-1258" data-mp="1uu2ggqtujfkx" />
    </div>;
}